import * as React from 'react'

import { KonvaEventObject } from 'konva/lib/Node'
import { ChevronDownIcon, ChevronUpIcon, MapPinnedIcon } from 'lucide-react'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, MobXProviderContext, observer, Provider } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { LocationType } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DeliveriesMapStore from '~/client/src/desktop/views/Deliveries/components/DeliveriesMap/DeliveriesMap.store'
import BaseMapView from '~/client/src/shared/components/BaseMapView/BaseMapView'
import DeliveryMapLayers from '~/client/src/shared/components/Deliveries/components/DeliveriesMapView/components/DeliveryMapLayers'
import KonvaDeliveryPins from '~/client/src/shared/components/Deliveries/components/DeliveriesMapView/components/KonvaDeliveryPins'
import * as Icons from '~/client/src/shared/components/Icons'
import LocationIcon from '~/client/src/shared/components/LocationIcon/LocationIcon'
import {
  buildDeliveryPinData,
  getDeliveryPinPopupAnchor,
  IDeliveryPinGroup,
} from '~/client/src/shared/components/LogisticsMapView/deliveryPinPoints'
import { popupPinScale } from '~/client/src/shared/components/LogisticsMapView/pinPopupAnchor'
import {
  buildSharedPinPoints,
  ISharedPinGroup,
} from '~/client/src/shared/components/LogisticsMapView/sharedPinPoints'
import { IWorkflowPinPoint } from '~/client/src/shared/components/LogisticsMapView/workflowPinPoints'
import DeliveryMapPopup from '~/client/src/shared/components/MapPopups/DeliveryMapPopup'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import SitemapDeliveriesDraggableModal from '~/client/src/shared/components/SitemapDraggableModalWrapper/components/SitemapDeliveriesDraggableModal'
import KonvaSitemapItems from '~/client/src/shared/components/SitemapHelpers/components/KonvaSitemapItems'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import FieldIds from '~/client/src/shared/enums/DeliveryFieldIds'
import DeliveryPinLocationOption, {
  getDeliveryPinLocationOptionDisplayName,
} from '~/client/src/shared/enums/DeliveryPinLocationOption'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import ICanvasImageCache from '~/client/src/shared/interfaces/ITextboxesCache'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Sitemap from '~/client/src/shared/models/Sitemap'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import SharedDeliveriesStore from '~/client/src/shared/stores/domain/SharedDeliveries.store'
import GlobeViewControlStore from '~/client/src/shared/stores/GlobeViewControl.store'
import { areObjectsEqual } from '~/client/src/shared/utils/util'

// localization: translated
interface IProps {
  deliveriesMapStore: DeliveriesMapStore
  textboxesCache: ICanvasImageCache

  state?: DesktopInitialState
  companiesStore?: CompaniesStore
  sharedDeliveriesStore?: SharedDeliveriesStore

  globeViewControlStore?: GlobeViewControlStore

  globe?: GlobeView
  sitemap?: Sitemap
}

interface IShowOnMenuOption {
  name: DeliveryPinLocationOption
  icon: JSX.Element
  fieldId?: FieldIds
}

const showOnMenuOptions: IShowOnMenuOption[] = [
  {
    fieldId: FieldIds.BUILDING,
    name: DeliveryPinLocationOption.building,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Building}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    fieldId: FieldIds.AREA,
    name: DeliveryPinLocationOption.area,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Area}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    fieldId: FieldIds.ZONE,
    name: DeliveryPinLocationOption.zone,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Zone}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    fieldId: FieldIds.GATE,
    name: DeliveryPinLocationOption.gate,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Gate}
        className="no-grow mx5"
      />
    ),
  },
  {
    fieldId: FieldIds.ROUTE,
    name: DeliveryPinLocationOption.route,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Route}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    fieldId: FieldIds.OFFLOADING_EQUIPMENT,
    name: DeliveryPinLocationOption.equipment,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Equipment}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    name: DeliveryPinLocationOption.mostPertinent,
    icon: <Icons.EyeView className="no-grow mx5 option-icon row y-center" />,
  },
  {
    fieldId: FieldIds.LEVEL,
    name: DeliveryPinLocationOption.level,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Level}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    fieldId: FieldIds.STAGING,
    name: DeliveryPinLocationOption.staging,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.Staging}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    fieldId: FieldIds.INTERIOR_DOOR,
    name: DeliveryPinLocationOption.interiorDoor,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.InteriorDoor}
        className="no-grow mx5 option-icon"
      />
    ),
  },
  {
    fieldId: FieldIds.INTERIOR_PATH,
    name: DeliveryPinLocationOption.interiorPath,
    icon: (
      <LocationIcon
        iconName={MapViewLocationIcon.InteriorPath}
        className="no-grow mx5 option-icon"
      />
    ),
  },
]
/* eslint-disable */
@inject('state', 'companiesStore', 'sharedDeliveriesStore')
@observer
export default class DeliveryMapView extends React.Component<IProps> {
  public static contextType = MobXProviderContext
  @observable private isShowOnMenuShown: boolean = false
  @observable private deliverySitemap: BaseMapView = null
  @observable private localContext = null
  @observable private localProps: IProps = null

  private containerRef: HTMLElement = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    const { deliveriesMapStore } = props

    deliveriesMapStore.resetAttrSelection()
    deliveriesMapStore.mapBoxViewerStore.setViewportFromAddress()
    deliveriesMapStore.mapBoxViewerStore.setDefaultMapMode()
    makeObservable(this)
  }

  public UNSAFE_componentWillMount(): void {
    this.localContext = this.context
  }

  public componentDidUpdate(): void {
    runInAction(() => {
      if (
        !this.props.sitemap ||
        !this.localProps.sitemap ||
        (this.localProps.sitemap &&
          this.props.sitemap &&
          !areObjectsEqual(this.localProps.sitemap, this.props.sitemap))
      )
        this.localProps.sitemap = this.props.sitemap
      if (
        !this.props.globe ||
        !this.localProps.globe ||
        (this.localProps.globe &&
          this.props.globe &&
          !areObjectsEqual(this.localProps.globe, this.props.globe))
      )
        this.localProps.globe = this.props.globe
    })
  }

  public componentDidMount() {
    this.validateShowOnOption()
  }

  public render() {
    const { deliveriesMapStore, state, globe, sitemap } = this.props
    const {
      isGlobeMode,
      selectedSitemapUrl,
      resetAttrSelection,
      resetAllFilters,
      selectDelivery,
      activeDeliveryId,
      attrIdToDeliveriesMap,
      selectedMapViewItem,
      mapBoxViewerStore,
      topOffset,
      leftOffset,
    } = deliveriesMapStore

    return (
      <section
        ref={this.setContainerRef}
        className="full-height full-width relative general-map-container"
      >
        <BaseMapView
          globe={globe}
          selectedSitemap={sitemap}
          isDeliveryView={true}
          sitemapUrl={selectedSitemapUrl}
          isDraggable={true}
          onWhiteboardClick={this.handleMouseDownOnContainer}
          onTouch={this.handleMouseDownOnContainer}
          mapBoxViewerStore={deliveriesMapStore.mapBoxViewerStore}
          projectAddress={state.projectAddress}
          ref={this.setDeliverySitemap}
        >
          {({ width, height }) => {
            if (!this.deliverySitemap) {
              return null
            }
            return (
              <Provider {...this.localContext}>
                {isGlobeMode
                  ? this.renderGlobeViewChildren()
                  : this.renderSitemapChildren(width, height)}
              </Provider>
            )
          }}
        </BaseMapView>
        {selectedMapViewItem && !isGlobeMode && (
          <SitemapDeliveriesDraggableModal
            onClose={resetAttrSelection}
            item={selectedMapViewItem}
            containerRef={this.containerRef}
            onDeliveryClick={selectDelivery}
            activeDeliveryId={activeDeliveryId}
            onFilterResetClick={resetAllFilters}
            relatedDeliveries={attrIdToDeliveriesMap[selectedMapViewItem.id]}
            topOffset={topOffset}
            leftOffset={leftOffset}
          />
        )}
        {this.renderShowOnMenu()}
      </section>
    )
  }

  @action
  private setDeliverySitemap = ref => {
    this.deliverySitemap = ref
  }

  private renderGlobeViewChildren = () => {
    const { mapBoxViewerStore, startDate, endDate } =
      this.localProps.deliveriesMapStore

    return (
      <DeliveryMapLayers
        mapBoxViewerStore={mapBoxViewerStore}
        deliveryPoints={this.deliveryPinData.points}
        sharedPoints={this.sharedPinData.points}
        onDeliveryPinClick={this.selectDeliveriesByCoordKey}
        onSharedPinClick={this.selectSharedDeliveryByCoordKey}
        onSelectItem={this.selectSitemapItem}
        selectedStartDate={startDate}
        selectedEndDate={endDate}
      >
        {this.renderGlobePopups}
      </DeliveryMapLayers>
    )
  }

  private get renderGlobePopups(): JSX.Element {
    const { deliveriesMapStore } = this.localProps
    const {
      resetAttrSelection,
      resetAllFilters,
      selectDelivery,
      activeDeliveryId,
      attrIdToDeliveriesMap,
      selectedMapViewItem,
      mapBoxViewerStore,
    } = deliveriesMapStore
    const relatedDeliveries = attrIdToDeliveriesMap[selectedMapViewItem?.id]
    const pinScale = popupPinScale(
      !!selectedMapViewItem,
      mapBoxViewerStore.globeViewStyle.isZoomScalingEnabled,
      () => mapBoxViewerStore.viewport?.zoom,
    )
    const pinAnchor = selectedMapViewItem
      ? getDeliveryPinPopupAnchor(this.deliveryPinData.points, pinScale)
      : null

    return (
      selectedMapViewItem && (
        <DeliveryMapPopup
          onClose={resetAttrSelection}
          item={selectedMapViewItem}
          onDeliveryClick={selectDelivery}
          activeDeliveryId={activeDeliveryId}
          onFilterResetClick={resetAllFilters}
          relatedDeliveries={relatedDeliveries}
          coordOverride={pinAnchor?.coord}
          offset={pinAnchor?.offset}
        />
      )
    )
  }

  @computed
  private get deliveryPinData(): {
    points: IWorkflowPinPoint[]
    groupsByCoordKey: Map<string, IDeliveryPinGroup>
  } {
    const {
      deliveriesMapStore: {
        isSelectedAttr,
        activeDeliveryId,
        closestAttrIdToDeliveriesMap,
        mapBoxViewerStore,
        deliverySitemapPinStore,
      },
      state: { user },
    } = this.localProps

    return buildDeliveryPinData(
      mapBoxViewerStore.displayedGlobeViewItems,
      closestAttrIdToDeliveriesMap,
      deliverySitemapPinStore,
      user,
      (deliveries, item) =>
        isSelectedAttr(item.id) ||
        deliveries.some(d => d.id === activeDeliveryId),
    )
  }

  // Deliveries shared in from another project. They carry no local marker, so they group by
  // their own coordinate and open the same shared-delivery view the DOM markers opened.
  @computed
  private get sharedPinData(): {
    points: IWorkflowPinPoint[]
    groupsByCoordKey: Map<string, ISharedPinGroup>
  } {
    return buildSharedPinPoints([], this.localProps.sharedDeliveriesStore.list)
  }

  @action.bound
  private selectSharedDeliveryByCoordKey(coordKey: string) {
    const group = this.sharedPinData.groupsByCoordKey.get(coordKey)
    const delivery = group?.deliveries[0]
    if (delivery) {
      this.localProps.deliveriesMapStore.selectSharedDelivery(delivery.id)
    }
  }

  @action.bound
  private selectDeliveriesByCoordKey(coordKey: string) {
    const group = this.deliveryPinData.groupsByCoordKey.get(coordKey)
    if (group) this.selectDeliveries(group.item)
  }

  @action.bound
  private selectDeliveries(item: MapViewItemBase) {
    this.selectSitemapItem(item)
  }

  private renderSitemapChildren = (
    width: number,
    height: number,
    isMainSitemap?: boolean,
  ) => {
    const { textboxesCache, deliveriesMapStore } = this.localProps

    const {
      activeDeliveryId,
      closestAttrIdToDeliveriesBoardMap,
      displayedSitemapItems,
      startDate,
      endDate,
    } = deliveriesMapStore

    return (
      <>
        <KonvaSitemapItems
          className={classList({ 'unclickable-element': !isMainSitemap })}
          items={displayedSitemapItems || []}
          containerWidth={width}
          containerHeight={height}
          textboxesCache={textboxesCache}
          onSelectItem={this.selectSitemapItem}
          selectedStartDate={startDate}
          selectedEndDate={endDate}
        />
        <KonvaDeliveryPins
          items={displayedSitemapItems}
          containerWidth={width}
          containerHeight={height}
          deliveriesByItemId={closestAttrIdToDeliveriesBoardMap}
          pinStore={deliveriesMapStore.deliverySitemapPinStore}
          user={this.localProps.state.user}
          companiesStore={this.localProps.companiesStore}
          isPinSelected={(deliveries, item) =>
            deliveriesMapStore.isSelectedAttr(item.id) ||
            deliveries.some(d => d.id === activeDeliveryId)
          }
          onPinClick={(_deliveries, item, event) =>
            this.onDeliveryPinClick(item, event)
          }
          onPinTouchEnd={(_deliveries, item, event) =>
            this.onDeliveryPinTouch(item, event)
          }
        />
      </>
    )
  }

  private onDeliveryPinTouch = (
    item: MapViewItemBase,
    event: KonvaEventObject<TouchEvent>,
  ) => {
    event.cancelBubble = true
    this.selectSitemapItem(item)
  }

  private onDeliveryPinClick = (
    item: MapViewItemBase,
    event: KonvaEventObject<MouseEvent>,
  ) => {
    event.cancelBubble = true
    this.selectSitemapItem(item, event.evt.layerY, event.evt.layerX)
  }

  private selectSitemapItem = (
    item: MapViewItemBase,
    y?: number,
    x?: number,
  ) => {
    const { isSitemapItemSupported, selectAttr } =
      this.localProps.deliveriesMapStore

    if (isSitemapItemSupported(item)) {
      selectAttr(item.id, y, x)
    }
  }

  private setContainerRef = (ref: HTMLElement) => {
    this.containerRef = ref
  }

  private handleMouseDownOnContainer = () => {
    const { resetAttrSelection, selectedMapViewItem } =
      this.localProps.deliveriesMapStore

    if (selectedMapViewItem) {
      resetAttrSelection()
    }
  }

  @computed
  private get locationOptions(): DeliveryPinLocationOption[] {
    const { displayedMapboxItems } = this.localProps.deliveriesMapStore

    const hasLevelOption = displayedMapboxItems.some(
      x => x?.dataObject?.type === LocationType.Level,
    )

    return Object.values(DeliveryPinLocationOption).filter(o =>
      o === DeliveryPinLocationOption.level ? hasLevelOption : true,
    )
  }

  private validateShowOnOption = () => {
    if (
      !this.locationOptions.includes(
        this.localProps.deliveriesMapStore.deliverySitemapPinStore
          .selectedPinOption,
      )
    ) {
      this.localProps.deliveriesMapStore.deliverySitemapPinStore.selectDefaultShowOnOption()
    }
  }

  private renderShowOnMenu = () => {
    const ChevronIcon = this.isShowOnMenuShown ? ChevronDownIcon : ChevronUpIcon

    const { selectedPinOption } =
      this.localProps.deliveriesMapStore.deliverySitemapPinStore
    const showOnMenuOption = showOnMenuOptions.find(
      o => o.name === selectedPinOption,
    )

    return (
      <div className="absolute row show-on-menu pointer">
        <div className="show-on-text-box text lp15 small uppercase pa5 br-light-input-border row y-center nowrap no-grow">
          <MapPinnedIcon size={16} className="shown-on-icon no-grow mr5" />
          {Localization.translator.showOn}
        </div>
        <div
          className={classList({
            'show-on-icons-box row': true,
            selected: this.isShowOnMenuShown,
          })}
          onClick={this.toggleShowOnMenu}
        >
          {showOnMenuOption.icon}
          <span className="large text">
            {this.getOptionName(showOnMenuOption)}
          </span>
          <ChevronIcon size={16} className="no-grow mx5 caret-icon" />
        </div>
        {this.isShowOnMenuShown && (
          <MenuCloser className="no-grow" closeMenu={this.toggleShowOnMenu}>
            <div className="absolute col show-on-options brada10">
              {this.locationOptions.map((option, index) => {
                const currentOption = showOnMenuOptions.find(
                  o => o.name === option,
                )
                const isSelected = option === selectedPinOption
                const isLastOption = index === this.locationOptions.length - 1

                return (
                  <div
                    onClick={this.selectShowOnOption.bind(this, option)}
                    key={option}
                    className={classList({
                      'bb-dark-grey-border py5 option-holder row y-center pl10  no-grow pointer': true,
                      selected: isSelected,
                      'first-option': !index,
                      'last-option': isLastOption,
                    })}
                  >
                    {currentOption.icon}
                    <span className="large text">
                      {this.getOptionName(currentOption)}
                    </span>
                    {isSelected && (
                      <Icons.CheckPaletteBlue className="no-grow ml10" />
                    )}
                  </div>
                )
              })}
            </div>
          </MenuCloser>
        )}
      </div>
    )
  }

  private selectShowOnOption = (option: DeliveryPinLocationOption) => {
    this.localProps.deliveriesMapStore.deliverySitemapPinStore.selectShowOnOption(
      option,
    )

    this.toggleShowOnMenu()
  }

  @action
  private toggleShowOnMenu = () => {
    this.isShowOnMenuShown = !this.isShowOnMenuShown
  }

  private getOptionName({ fieldId, name }: IShowOnMenuOption): string {
    const { state } = this.localProps
    return fieldId
      ? state.getDeliveryFieldName(fieldId)
      : getDeliveryPinLocationOptionDisplayName(name)
  }
}
